import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Divider, styled } from "@mui/material";
import {
  Button,
  convertPxToRem,
  Input,
  Link,
} from "@treez-inc/component-library";
import GoogleButton from "components/GoogleButton";
import LoadingSpinner from "components/LoadingSpinner";
import StyledForm from "components/StyledForm";
import useToken from "hooks/useToken";
import useCode from "hooks/useCode";
import useMSOAppData from "hooks/useMSOAppData";
import useRedirectUrlToken from "hooks/useRedirectUrlToken";
import useRedirectUrlCode from "hooks/useRedirectUrlCode";
import useGoogleSSO from "hooks/useGoogleSSO";
import handleLoginSubmit from "./utils/handleLoginSubmit";
import handleReturningUserLogin from "./utils/handleReturningUserLogin";
import { APPLICATION_ID_CONSTS } from "../../constants";

const StyledLink = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: convertPxToRem(8),
});

const ButtonContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "showGoogleButton",
})<{ showGoogleButton: boolean }>(({ showGoogleButton }) => ({
  button: {
    width: "100%",
  },
  marginTop: convertPxToRem(16),
  marginBottom: showGoogleButton ? convertPxToRem(16) : convertPxToRem(24),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginTop: convertPxToRem(16),
  marginBottom: convertPxToRem(16),
  "&.MuiDivider-withChildren": {
    ...theme.typography.mediumTextStrong,
  },
}));

const StyledPasswordInputContainer = styled("div")({
  marginTop: convertPxToRem(16),
});

const LoginView: React.FC = () => {
  const { applicationId, callbackUrl } = useMSOAppData();
  const { saveToken, callbackWithUrlToken } = useToken({
    callbackUrl: callbackUrl!,
  });
  const { saveCode, callbackWithUrlCode } = useCode({
    callbackUrl: callbackUrl!,
  });
  // intentionally set isPageLoading to true instead of using the usePageLoading hook to fix flicker
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const { googleSSOUrl } = useGoogleSSO({ applicationId });

  useEffect(() => {
    // if user is already logs in and navigates to the login page
    if (applicationId) {
      handleReturningUserLogin({
        applicationId,
        saveToken,
        saveCode,
        setFormError,
        setIsPageLoading,
      });
    }
  }, [applicationId]);

  useEffect(() => {
    document.title = "Treez | Sign In";
  }, []);

  useRedirectUrlCode({ callbackWithUrlCode });

  // redirect to callback URL for MSO apps when tokens are updated
  useRedirectUrlToken({ callbackWithUrlToken });

  // Only Ask Treez & EPM (plan & brand) should be able to use Google SSO
  const showGoogleButton =
    (applicationId && applicationId?.includes(APPLICATION_ID_CONSTS.EPM)) ||
    applicationId?.includes(APPLICATION_ID_CONSTS.ASK_Treez);

  const { handleSubmit, control } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    setFormError(null);
    setIsPageLoading(true);

    await handleLoginSubmit({
      email,
      password,
      applicationId,
      urlSearchParams,
      saveToken,
      saveCode,
      setFormError,
    });

    setIsPageLoading(false);
  };

  return (
    <StyledForm pageTitle="Sign in" formSubmitError={formError}>
      {isPageLoading && <LoadingSpinner />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="email"
          control={control}
          rules={{
            required: "Email is required",
          }}
          render={({ field, fieldState: { error } }) => {
            // treez Input component doesn't accept a ref prop
            const { ref, ...rest } = field;
            return (
              <Input
                {...rest}
                label="Email"
                error={!!error}
                helperText={error?.message}
                testId="email-input"
              />
            );
          }}
        />

        <Controller
          control={control}
          name="password"
          rules={{
            required: "Password is required",
          }}
          render={({ field, fieldState: { error } }) => {
            // treez Input component doesn't accept a ref prop
            const { ref, ...rest } = field;
            return (
              <StyledPasswordInputContainer>
                <Input
                  {...rest}
                  testId="password-input"
                  label="Password"
                  type="password"
                  helperText={error?.message}
                  error={!!error}
                />
              </StyledPasswordInputContainer>
            );
          }}
        />
        <StyledLink data-testid="forgot-password-wrapper">
          <Link
            testId="forgot-password-link"
            ariaLabel="forgot password link"
            href="/forgot-password"
            underline="always"
          >
            Forgot password?
          </Link>
        </StyledLink>
        <input type="submit" hidden />
        <ButtonContainer showGoogleButton={showGoogleButton!}>
          <Button
            variant="primary"
            label="Sign in"
            onClick={handleSubmit(onSubmit)}
            testId="sign-in-button"
          />
        </ButtonContainer>
      </form>
      {showGoogleButton && (
        <>
          <StyledDivider data-testid="divider">Or</StyledDivider>
          <GoogleButton
            label="Continue with Google"
            googleSSOUrl={googleSSOUrl}
          />
        </>
      )}
    </StyledForm>
  );
};

export default LoginView;
